// Constancia de con qué cuenta inició sesión ESTE navegador.
//
// El problema que resuelve: /subscribe y /change-password aceptan la identidad
// que afirma un enlace (`id_token_hint`). Si ese enlace llega a un navegador
// que no es el del titular, quien lo abre opera —y paga— dentro de una cuenta
// ajena sin enterarse. Casi siempre esa persona inició sesión como ella misma,
// en este mismo navegador, un rato antes.
//
// `POST /auth/login` devuelve un `browser_ticket` firmado por el backend. Lo
// guardamos y lo mandamos junto con el enlace: si el enlace dice una cuenta y
// el ticket otra, el backend responde `account_mismatch` y no entrega sesión.
//
// NO es una credencial: no autentica nada ni abre ninguna sesión. Solo le sirve
// al backend para comparar. Por eso puede vivir en localStorage (tiene que
// sobrevivir a que el usuario cierre la pestaña del login y abra el enlace en
// otra), cosa que jamás haríamos con un token.
//
// Clave por partner: una cuenta de un partner no dice nada sobre otro.
//
// El storage puede no existir o tirar (Safari privado, cookies bloqueadas). En
// ese caso simplemente no hay ticket, y el backend trata el enlace como "sin
// constancia": la pantalla pide confirmar la cuenta antes de operar.

const PREFIX = 'authsys:browser-ticket:';

// Un ticket firmado mide unos cientos de bytes. Cualquier cosa mucho más grande
// no es un ticket: no se guarda ni se manda.
const MAX_TICKET_LENGTH = 4096;

function keyFor(partnerId: string): string | null {
  return partnerId ? `${PREFIX}${partnerId}` : null;
}

/** ¿Tiene forma de ticket? (string no vacío, sin espacios, de tamaño razonable). */
export function isPlausibleTicket(ticket: unknown): ticket is string {
  return (
    typeof ticket === 'string' &&
    ticket.length > 0 &&
    ticket.length <= MAX_TICKET_LENGTH &&
    !/\s/.test(ticket)
  );
}

const BROWSER_ID_KEY = 'authsys:browser-id';
let volatileBrowserId: string | null = null;

/**
 * Identificador aleatorio y estable de este navegador. El backend asocia cada
 * enlace con id_token_hint al primer navegador que lo abre: el mismo puede
 * recargar, otro es rechazado. No identifica a la persona ni viaja a terceros.
 * Sin almacenamiento (modo privado estricto) dura lo que la página.
 */
export function getBrowserId(): string {
  try {
    const saved = localStorage.getItem(BROWSER_ID_KEY);
    if (saved && saved.length >= 8 && saved.length <= 100) return saved;
    const fresh = crypto.randomUUID();
    localStorage.setItem(BROWSER_ID_KEY, fresh);
    return fresh;
  } catch {
    volatileBrowserId ??=
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `v-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    return volatileBrowserId;
  }
}

/** Guarda el ticket del último login de este navegador para el partner. */
export function saveBrowserTicket(partnerId: string, ticket: unknown): void {
  const key = keyFor(partnerId);
  if (!key || !isPlausibleTicket(ticket)) return;
  try {
    localStorage.setItem(key, ticket);
  } catch {
    // Sin storage: no hay constancia. Degradación, no error.
  }
}

/** Ticket guardado para el partner, o null si no hay (o no se puede leer). */
export function readBrowserTicket(partnerId: string): string | null {
  const key = keyFor(partnerId);
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    return isPlausibleTicket(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function clearBrowserTicket(partnerId: string): void {
  const key = keyFor(partnerId);
  if (!key) return;
  try {
    localStorage.removeItem(key);
  } catch {
    // nada que limpiar
  }
}
